import { useState, useRef, useCallback } from "react";
import { Mic, MicOff, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

export type VoiceMode = "session" | "planner";

export interface VoiceResult {
  transcript: string;
  parsed: unknown;
  mode: VoiceMode;
}

interface VoiceMicButtonProps {
  mode: VoiceMode;
  onResult: (result: VoiceResult) => void;
  onError?: (msg: string) => void;
  /** Extra context passed to the server parser (e.g. current exercise name) */
  context?: string;
  className?: string;
  size?: "sm" | "md" | "lg";
  disabled?: boolean;
}

type MicState = "idle" | "listening" | "processing" | "error";

const SIZE_MAP = {
  sm: "h-9 w-9",
  md: "h-12 w-12",
  lg: "h-14 w-14",
};

const ICON_SIZE_MAP = {
  sm: 16,
  md: 20,
  lg: 22,
};

export function VoiceMicButton({
  mode,
  onResult,
  onError,
  context,
  className,
  size = "md",
  disabled = false,
}: VoiceMicButtonProps) {
  const [state, setState] = useState<MicState>("idle");
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const handlePress = useCallback(async () => {
    if (disabled) return;

    // If already listening, stop and process
    if (state === "listening") {
      stopRecording();
      return;
    }

    if (state === "processing") return;

    // Start recording
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      // Prefer webm/opus; fall back to whatever the browser supports
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "audio/ogg";

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        setState("processing");
        try {
          const blob = new Blob(chunksRef.current, { type: mimeType });
          const formData = new FormData();
          const ext = mimeType.includes("ogg") ? "ogg" : "webm";
          formData.append("audio", blob, `recording.${ext}`);
          formData.append("mode", mode);
          if (context) formData.append("context", context);

          const res = await fetch("/api/voice/transcribe", {
            method: "POST",
            body: formData,
            credentials: "include",
          });

          if (!res.ok) {
            const err = await res.text();
            throw new Error(err || "Transcription failed");
          }

          const data = await res.json() as { transcript: string; parsed: unknown };
          setState("idle");
          onResult({ transcript: data.transcript, parsed: data.parsed, mode });
        } catch (err) {
          setState("error");
          const msg = err instanceof Error ? err.message : "Voice processing failed";
          onError?.(msg);
          // Auto-reset error state after 2s
          setTimeout(() => setState("idle"), 2000);
        }
      };

      recorder.start();
      setState("listening");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Microphone access denied";
      setState("error");
      onError?.(msg);
      setTimeout(() => setState("idle"), 2000);
    }
  }, [disabled, state, stopRecording, mode, context, onResult, onError]);

  const isListening = state === "listening";
  const isProcessing = state === "processing";
  const isError = state === "error";

  return (
    <button
      type="button"
      onClick={handlePress}
      disabled={disabled || isProcessing}
      aria-label={isListening ? "Stop recording" : "Start voice input"}
      className={cn(
        "relative flex items-center justify-center rounded-full transition-all duration-200",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-forge-accent",
        SIZE_MAP[size],
        // Base styles
        !isListening && !isError && !isProcessing &&
          "bg-forge-surface border border-forge-border text-forge-muted hover:text-forge-accent hover:border-forge-accent active:scale-95",
        // Listening: pulsing green ring
        isListening &&
          "bg-green-500/20 border-2 border-green-400 text-green-400 animate-pulse",
        // Processing: spinner state
        isProcessing &&
          "bg-forge-surface border border-forge-border text-forge-muted cursor-not-allowed",
        // Error: amber flash
        isError &&
          "bg-amber-500/20 border-2 border-amber-400 text-amber-400",
        disabled && "opacity-40 cursor-not-allowed",
        className
      )}
    >
      {isProcessing ? (
        <Loader2 size={ICON_SIZE_MAP[size]} className="animate-spin" />
      ) : isListening ? (
        <MicOff size={ICON_SIZE_MAP[size]} />
      ) : (
        <Mic size={ICON_SIZE_MAP[size]} />
      )}

      {/* Outer pulse ring when listening */}
      {isListening && (
        <span className="absolute inset-0 rounded-full border-2 border-green-400 animate-ping opacity-60" />
      )}
    </button>
  );
}
